import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  TableChart as TableIcon,
  BarChart as ChartIcon,
  Label as FieldKVIcon,
  Tune as ControlIcon,
  Image as ImageIcon,
  Assignment as FormIcon,
  Map as MapIcon,
  Memory as DigitalTwinIcon,
  Chat as AIChatIcon,
} from '@mui/icons-material';

import { useCreateWidget } from '../../data/canvas/useWidgets';
import {
  CreateWidgetRequest,
  WidgetType,
  Canvas,
  Page,
} from '../../types/canvas.types';
import { useWidgetDataSchema } from '../../data/canvas/useWidgetData';
import { useEntityFields } from '../../data/canvas/useDatastoreSchemas';
import { DatastoreEntitySelector } from './DatastoreEntitySelector';

interface WidgetWizardProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  pageId: string;
  canvas?: Canvas;
  page?: Page;
}

interface WidgetTypeOption {
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  category: string;
}

const widgetTypes: WidgetTypeOption[] = [
  {
    value: 'table',
    label: 'Table Widget',
    description: 'Display data in rows and columns',
    icon: <TableIcon />,
    category: 'Data Display',
  },
  {
    value: 'chart',
    label: 'Chart Widget',
    description: 'Visualize data with charts and graphs',
    icon: <ChartIcon />,
    category: 'Data Display',
  },
  {
    value: 'field_kv',
    label: 'Field/Value Widget',
    description: 'Show key-value pairs or metrics',
    icon: <FieldKVIcon />,
    category: 'Data Display',
  },
  {
    value: 'control',
    label: 'Control Widget',
    description: 'Interactive controls and filters',
    icon: <ControlIcon />,
    category: 'Interactive',
  },
  {
    value: 'image',
    label: 'Image Widget',
    description: 'Display images and media',
    icon: <ImageIcon />,
    category: 'Media',
  },
  {
    value: 'form',
    label: 'Form Widget',
    description: 'Input forms and data entry',
    icon: <FormIcon />,
    category: 'Interactive',
  },
  {
    value: 'map',
    label: 'Map Widget',
    description: 'Geographic maps and location data',
    icon: <MapIcon />,
    category: 'Specialized',
  },
  {
    value: 'digital_twin',
    label: 'Digital Twin Widget',
    description: 'Digital twin visualizations',
    icon: <DigitalTwinIcon />,
    category: 'Specialized',
  },
  {
    value: 'ai_chat',
    label: 'AI Chat Widget',
    description: 'AI conversation interface',
    icon: <AIChatIcon />,
    category: 'AI',
  },
];

const CHART_TYPES = [
  { value: 'line', label: 'Line Chart', description: 'Time series and trends' },
  { value: 'bar', label: 'Bar Chart', description: 'Categorical comparison' },
  {
    value: 'pie',
    label: 'Pie Chart',
    description: 'Part-to-whole relationships',
  },
  {
    value: 'scatter',
    label: 'Scatter Chart',
    description: 'Correlation analysis',
  },
  {
    value: 'area',
    label: 'Area Chart',
    description: 'Time series with emphasis',
  },
];

export const WidgetWizard: React.FC<WidgetWizardProps> = ({
  open,
  onClose,
  accountId,
  pageId,
  canvas,
  page,
}) => {
  // Get effective datastore ID (page override or canvas default)
  const effectiveDatastoreId = page?.datastore_id || canvas?.datastore_id || '';

  const [activeStep, setActiveStep] = useState(0);
  const [selectedType, setSelectedType] = useState<string>('');
  const [widgetConfig, setWidgetConfig] = useState({
    title: '',
    description: '',
    position: { x: 0, y: 0, width: 4, height: 3 },
  });
  const [dataSource, setDataSource] = useState({
    datastore_id: effectiveDatastoreId,
    entity_name: '',
    // Legacy API service fields (kept for backward compatibility)
    api_service_name: 'dorothy-data-api',
    api_version: 'v1',
  });
  const [chartConfig, setChartConfig] = useState({
    chart_type: 'line',
    x_field: '',
    y_field: '',
    category_field: '',
    show_legend: true,
  });

  const [imageConfig, setImageConfig] = useState({
    image_url: '',
    alt_text: '',
    caption: '',
    fit_mode: 'contain' as 'contain' | 'cover' | 'fill',
    lightbox_enabled: true,
  });

  const [controlConfig, setControlConfig] = useState({
    filters: [] as Array<{
      field: string;
      label: string;
      type: 'select' | 'multi-select' | 'text' | 'date' | 'date-range';
      options?: Array<{ label: string; value: any }>;
    }>,
  });

  const createWidgetMutation = useCreateWidget(pageId, accountId);

  // Fetch available fields from datastore metadata
  const { fields: availableFields, isLoading: fieldsLoading } = useEntityFields(
    accountId,
    dataSource.datastore_id,
    dataSource.entity_name,
    !!dataSource.datastore_id && !!dataSource.entity_name,
  );

  const handleTypeSelect = (widgetType: string) => {
    setSelectedType(widgetType);
    const typeOption = widgetTypes.find((t) => t.value === widgetType);
    if (typeOption) {
      setWidgetConfig((prev) => ({
        ...prev,
        title: typeOption.label.replace(' Widget', ''),
      }));
    }
    setActiveStep(1); // Move to configuration step
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreate = async () => {
    if (!selectedType) return;

    // Build configuration based on widget type
    const configuration: any = {
      title: widgetConfig.title,
      description: widgetConfig.description,
    };

    // Add chart-specific configuration
    if (selectedType === 'chart') {
      configuration.chart_type = chartConfig.chart_type;
      configuration.x_field = chartConfig.x_field;
      configuration.y_field = chartConfig.y_field;
      if (chartConfig.category_field) {
        configuration.category_field = chartConfig.category_field;
      }
      configuration.show_legend = chartConfig.show_legend;
    }

    // Add image-specific configuration
    if (selectedType === 'image') {
      configuration.image_url = imageConfig.image_url;
      configuration.alt_text = imageConfig.alt_text;
      configuration.caption = imageConfig.caption;
      configuration.fit_mode = imageConfig.fit_mode;
      configuration.lightbox_enabled = imageConfig.lightbox_enabled;
    }

    // Add control-specific configuration
    if (selectedType === 'control') {
      configuration.filters = controlConfig.filters;
    }

    const widgetData: CreateWidgetRequest = {
      widget_type: selectedType as WidgetType,
      position: widgetConfig.position,
      configuration,
      data_source: dataSource.entity_name ? dataSource : {},
      order: 0,
    };

    try {
      await createWidgetMutation.mutateAsync(widgetData);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error creating widget:', error);
    }
  };

  const resetForm = () => {
    setActiveStep(0);
    setSelectedType('');
    setWidgetConfig({
      title: '',
      description: '',
      position: { x: 0, y: 0, width: 4, height: 3 },
    });
    setDataSource({
      datastore_id: effectiveDatastoreId,
      entity_name: '',
      api_service_name: 'dorothy-data-api',
      api_version: 'v1',
    });
    setChartConfig({
      chart_type: 'line',
      x_field: '',
      y_field: '',
      category_field: '',
      show_legend: true,
    });
    setImageConfig({
      image_url: '',
      alt_text: '',
      caption: '',
      fit_mode: 'contain',
      lightbox_enabled: true,
    });
    setControlConfig({
      filters: [],
    });
  };

  const handleCancel = () => {
    onClose();
    resetForm();
  };

  const needsDataSource =
    selectedType === 'chart' ||
    selectedType === 'table' ||
    selectedType === 'field_kv';
  const isControlWidget = selectedType === 'control';
  const isImageWidget = selectedType === 'image';

  const steps = needsDataSource
    ? ['Select Type', 'Configure Widget', 'Data Source', 'Review']
    : isControlWidget
      ? [
          'Select Type',
          'Configure Widget',
          'Data Source',
          'Configure Filters',
          'Review',
        ]
      : isImageWidget
        ? ['Select Type', 'Configure Widget', 'Image Settings', 'Review']
        : ['Select Type', 'Configure Widget', 'Review'];

  const canProceedFromConfig = widgetConfig.title.trim().length > 0;
  const canProceedFromDataSource =
    (!needsDataSource && !isControlWidget) ||
    dataSource.entity_name.trim().length > 0;
  const canProceedFromChart =
    selectedType !== 'chart' || (chartConfig.x_field && chartConfig.y_field);

  const groupedWidgets = widgetTypes.reduce(
    (acc, widget) => {
      if (!acc[widget.category]) {
        acc[widget.category] = [];
      }
      acc[widget.category].push(widget);
      return acc;
    },
    {} as Record<string, WidgetTypeOption[]>,
  );

  const isLoading = createWidgetMutation.isPending;
  const error = createWidgetMutation.error;

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        // Step 1: Widget Type Selection
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Choose Widget Type
            </Typography>

            {Object.entries(groupedWidgets).map(([category, widgets]) => (
              <Box key={category} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  {category}
                </Typography>
                <Grid container spacing={2}>
                  {widgets.map((widget) => (
                    <Grid item xs={12} sm={6} md={4} key={widget.value}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 3,
                            borderColor: 'primary.main',
                          },
                        }}
                        onClick={() => handleTypeSelect(widget.value)}
                      >
                        <CardContent>
                          <Box display="flex" alignItems="center" mb={1}>
                            {widget.icon}
                            <Typography variant="h6" sx={{ ml: 1 }}>
                              {widget.label}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {widget.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        );

      case 1:
        // Step 2: Widget Configuration
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure{' '}
              {widgetTypes.find((w) => w.value === selectedType)?.label}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Widget Title"
                  value={widgetConfig.title}
                  onChange={(e) =>
                    setWidgetConfig((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  placeholder="Enter widget title"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={widgetConfig.description}
                  onChange={(e) =>
                    setWidgetConfig((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Enter widget description"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        // Step 3: Image Settings / Data Source / Review depending on type
        if (isImageWidget) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Image Settings
              </Typography>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Image URL"
                    value={imageConfig.image_url}
                    onChange={(e) =>
                      setImageConfig((prev) => ({
                        ...prev,
                        image_url: e.target.value,
                      }))
                    }
                    placeholder="https://example.com/image.png"
                    required
                    helperText="Enter an external URL or blobstore signed URL"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Alt Text"
                    value={imageConfig.alt_text}
                    onChange={(e) =>
                      setImageConfig((prev) => ({
                        ...prev,
                        alt_text: e.target.value,
                      }))
                    }
                    placeholder="Describe the image for accessibility"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Caption (Optional)"
                    value={imageConfig.caption}
                    onChange={(e) =>
                      setImageConfig((prev) => ({
                        ...prev,
                        caption: e.target.value,
                      }))
                    }
                    placeholder="Caption text displayed below the image"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Fit Mode</InputLabel>
                    <Select
                      value={imageConfig.fit_mode}
                      label="Fit Mode"
                      onChange={(e: SelectChangeEvent) =>
                        setImageConfig((prev) => ({
                          ...prev,
                          fit_mode: e.target.value as
                            | 'contain'
                            | 'cover'
                            | 'fill',
                        }))
                      }
                    >
                      <MenuItem value="contain">
                        <Box>
                          <Typography variant="body1">Contain</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Fit within bounds, preserve aspect ratio
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="cover">
                        <Box>
                          <Typography variant="body1">Cover</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Fill bounds, crop if needed
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="fill">
                        <Box>
                          <Typography variant="body1">Fill</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Stretch to fill bounds
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Lightbox</InputLabel>
                    <Select
                      value={imageConfig.lightbox_enabled ? 'yes' : 'no'}
                      label="Lightbox"
                      onChange={(e: SelectChangeEvent) =>
                        setImageConfig((prev) => ({
                          ...prev,
                          lightbox_enabled: e.target.value === 'yes',
                        }))
                      }
                    >
                      <MenuItem value="yes">Enabled (click to expand)</MenuItem>
                      <MenuItem value="no">Disabled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {imageConfig.image_url && (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Preview
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                        maxHeight: 200,
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Box
                        component="img"
                        src={imageConfig.image_url}
                        alt={imageConfig.alt_text || 'Preview'}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 180,
                          objectFit: 'contain',
                        }}
                        onError={(e: any) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        } else if (needsDataSource && selectedType !== 'chart') {
          // Data source step for tables
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Data Source
              </Typography>

              {!effectiveDatastoreId ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No datastore configured for this canvas. Please configure a
                  datastore in canvas settings.
                </Alert>
              ) : (
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <DatastoreEntitySelector
                      accountId={accountId}
                      datastoreId={effectiveDatastoreId}
                      value={dataSource.entity_name}
                      onChange={(entityName, entity) => {
                        setDataSource((prev) => ({
                          ...prev,
                          entity_name: entityName,
                        }));
                      }}
                      label="Table or View"
                      required
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          );
        } else if (isControlWidget) {
          // Data source step for control widgets
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Data Source
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a table or view to get available fields for filtering
              </Typography>

              {!effectiveDatastoreId ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No datastore configured for this canvas. Please configure a
                  datastore in canvas settings.
                </Alert>
              ) : (
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <DatastoreEntitySelector
                      accountId={accountId}
                      datastoreId={effectiveDatastoreId}
                      value={dataSource.entity_name}
                      onChange={(entityName, entity) => {
                        setDataSource((prev) => ({
                          ...prev,
                          entity_name: entityName,
                        }));
                      }}
                      label="Table or View"
                      required
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          );
        } else if (selectedType === 'chart') {
          // Data source for charts
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Data Source
              </Typography>

              {!effectiveDatastoreId ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No datastore configured for this canvas. Please configure a
                  datastore in canvas settings.
                </Alert>
              ) : (
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <DatastoreEntitySelector
                      accountId={accountId}
                      datastoreId={effectiveDatastoreId}
                      value={dataSource.entity_name}
                      onChange={(entityName, entity) => {
                        setDataSource((prev) => ({
                          ...prev,
                          entity_name: entityName,
                        }));
                      }}
                      label="Table or View"
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Chart Type</InputLabel>
                      <Select
                        value={chartConfig.chart_type}
                        label="Chart Type"
                        onChange={(e: SelectChangeEvent) =>
                          setChartConfig((prev) => ({
                            ...prev,
                            chart_type: e.target.value,
                          }))
                        }
                      >
                        {CHART_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box>
                              <Typography variant="body1">
                                {type.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {type.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>
          );
        } else {
          // Review step for widgets without data source
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Review & Create
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Widget Type
                  </Typography>
                  <Typography variant="body1">
                    {widgetTypes.find((w) => w.value === selectedType)?.label}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Title
                  </Typography>
                  <Typography variant="body1">{widgetConfig.title}</Typography>
                </Grid>

                {widgetConfig.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {widgetConfig.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        }

      case 3:
        // Step 4: Image Review / Control Filter Config / Chart Fields / Table Review
        if (isImageWidget) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Review & Create
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Widget Type
                  </Typography>
                  <Typography variant="body1">Image Widget</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Title
                  </Typography>
                  <Typography variant="body1">{widgetConfig.title}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Image URL
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: 'break-all',
                      color: 'primary.main',
                    }}
                  >
                    {imageConfig.image_url || 'Not set'}
                  </Typography>
                </Grid>

                {imageConfig.alt_text && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Alt Text
                    </Typography>
                    <Typography variant="body1">
                      {imageConfig.alt_text}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Fit Mode
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {imageConfig.fit_mode}
                  </Typography>
                </Grid>

                {imageConfig.caption && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Caption
                    </Typography>
                    <Typography variant="body1">
                      {imageConfig.caption}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        } else if (isControlWidget) {
          // Filter configuration for control widgets
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Configure Filters
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add filters to allow users to control dashboard data
              </Typography>

              {controlConfig.filters.map((filter, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Field</InputLabel>
                        <Select
                          value={filter.field}
                          label="Field"
                          onChange={(e: SelectChangeEvent) => {
                            const newFilters = [...controlConfig.filters];
                            newFilters[index].field = e.target.value;
                            // Auto-populate label from field name
                            if (!newFilters[index].label) {
                              newFilters[index].label = e.target.value
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (l) => l.toUpperCase());
                            }
                            setControlConfig({ filters: newFilters });
                          }}
                        >
                          {availableFields &&
                            availableFields.map((field) => (
                              <MenuItem key={field} value={field}>
                                {field}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Display Label"
                        value={filter.label}
                        onChange={(e) => {
                          const newFilters = [...controlConfig.filters];
                          newFilters[index].label = e.target.value;
                          setControlConfig({ filters: newFilters });
                        }}
                        placeholder="e.g., Region"
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={filter.type}
                          label="Type"
                          onChange={(e: SelectChangeEvent) => {
                            const newFilters = [...controlConfig.filters];
                            newFilters[index].type = e.target.value as any;
                            setControlConfig({ filters: newFilters });
                          }}
                        >
                          <MenuItem value="select">Select</MenuItem>
                          <MenuItem value="multi-select">Multi-Select</MenuItem>
                          <MenuItem value="text">Text</MenuItem>
                          <MenuItem value="date">Date</MenuItem>
                          <MenuItem value="date-range">Date Range</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {(filter.type === 'select' ||
                      filter.type === 'multi-select') && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Options (comma-separated)"
                          placeholder="e.g., North America, Europe, Asia"
                          helperText="Enter values separated by commas"
                          size="small"
                          onChange={(e) => {
                            const values = e.target.value
                              .split(',')
                              .map((v) => v.trim())
                              .filter((v) => v);
                            const newFilters = [...controlConfig.filters];
                            newFilters[index].options = values.map((v) => ({
                              label: v,
                              value: v,
                            }));
                            setControlConfig({ filters: newFilters });
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const newFilters = controlConfig.filters.filter(
                            (_, i) => i !== index,
                          );
                          setControlConfig({ filters: newFilters });
                        }}
                      >
                        Remove Filter
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ))}

              <Button
                variant="outlined"
                onClick={() => {
                  setControlConfig({
                    filters: [
                      ...controlConfig.filters,
                      { field: '', label: '', type: 'select', options: [] },
                    ],
                  });
                }}
              >
                + Add Filter
              </Button>

              {fieldsLoading && (
                <Box
                  display="flex"
                  justifyContent="center"
                  p={3}
                  sx={{ mt: 2 }}
                >
                  <CircularProgress />
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ ml: 2 }}
                  >
                    Loading available fields...
                  </Typography>
                </Box>
              )}

              {!availableFields ||
                (availableFields.length === 0 && !fieldsLoading && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Select a data source in the previous step to see available
                    fields.
                  </Alert>
                ))}
            </Box>
          );
        } else if (selectedType === 'chart') {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Configure Chart Fields
              </Typography>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                {fieldsLoading ? (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress />
                    </Box>
                    <Typography align="center" color="text.secondary">
                      Loading available fields...
                    </Typography>
                  </Grid>
                ) : availableFields && availableFields.length > 0 ? (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>X-Axis Field</InputLabel>
                        <Select
                          value={chartConfig.x_field}
                          label="X-Axis Field"
                          onChange={(e: SelectChangeEvent) =>
                            setChartConfig((prev) => ({
                              ...prev,
                              x_field: e.target.value,
                            }))
                          }
                        >
                          {availableFields.map((field) => (
                            <MenuItem key={field} value={field}>
                              {field}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Y-Axis Field</InputLabel>
                        <Select
                          value={chartConfig.y_field}
                          label="Y-Axis Field"
                          onChange={(e: SelectChangeEvent) =>
                            setChartConfig((prev) => ({
                              ...prev,
                              y_field: e.target.value,
                            }))
                          }
                        >
                          {availableFields.map((field) => (
                            <MenuItem key={field} value={field}>
                              {field}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Category Field (Optional)</InputLabel>
                        <Select
                          value={chartConfig.category_field}
                          label="Category Field (Optional)"
                          onChange={(e: SelectChangeEvent) =>
                            setChartConfig((prev) => ({
                              ...prev,
                              category_field: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="">None</MenuItem>
                          {availableFields.map((field) => (
                            <MenuItem key={field} value={field}>
                              {field}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Show Legend</InputLabel>
                        <Select
                          value={chartConfig.show_legend ? 'yes' : 'no'}
                          label="Show Legend"
                          onChange={(e: SelectChangeEvent) =>
                            setChartConfig((prev) => ({
                              ...prev,
                              show_legend: e.target.value === 'yes',
                            }))
                          }
                        >
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Enter an entity name in the previous step to load
                      available fields.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        } else {
          // Review step for tables
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Review & Create
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Widget Type
                  </Typography>
                  <Typography variant="body1">
                    {widgetTypes.find((w) => w.value === selectedType)?.label}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Title
                  </Typography>
                  <Typography variant="body1">{widgetConfig.title}</Typography>
                </Grid>

                {widgetConfig.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {widgetConfig.description}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data Source
                  </Typography>
                  <Typography variant="body1">
                    {dataSource.api_service_name} / {dataSource.entity_name}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          );
        }

      case 4:
        // Step 5: Final Review (charts and controls)
        if (isControlWidget) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Review & Create
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Widget Type
                  </Typography>
                  <Typography variant="body1">
                    {widgetTypes.find((w) => w.value === selectedType)?.label}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Title
                  </Typography>
                  <Typography variant="body1">{widgetConfig.title}</Typography>
                </Grid>

                {widgetConfig.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {widgetConfig.description}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data Source
                  </Typography>
                  <Typography variant="body1">
                    {dataSource.entity_name}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Filters ({controlConfig.filters.length})
                  </Typography>
                  {controlConfig.filters.length > 0 ? (
                    <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                      {controlConfig.filters.map((filter, index) => (
                        <li key={index}>
                          <Typography variant="body2">
                            <strong>{filter.label || filter.field}</strong> (
                            {filter.type})
                            {filter.options && filter.options.length > 0 && (
                              <>
                                {' '}
                                -{' '}
                                {filter.options.map((o) => o.label).join(', ')}
                              </>
                            )}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No filters configured
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          );
        }

        // Chart review
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Create
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Widget Type
                </Typography>
                <Typography variant="body1">
                  {widgetTypes.find((w) => w.value === selectedType)?.label}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Title
                </Typography>
                <Typography variant="body1">{widgetConfig.title}</Typography>
              </Grid>

              {widgetConfig.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {widgetConfig.description}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Data Source
                </Typography>
                <Typography variant="body1">
                  {dataSource.api_service_name} / {dataSource.entity_name}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Chart Type
                </Typography>
                <Typography variant="body1">
                  {
                    CHART_TYPES.find((t) => t.value === chartConfig.chart_type)
                      ?.label
                  }
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  X-Axis
                </Typography>
                <Typography variant="body1">{chartConfig.x_field}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Y-Axis
                </Typography>
                <Typography variant="body1">{chartConfig.y_field}</Typography>
              </Grid>

              {chartConfig.category_field && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category Field
                  </Typography>
                  <Typography variant="body1">
                    {chartConfig.category_field}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const getMaxStep = () => {
    if (isImageWidget) return 3; // Type, Config, ImageSettings, Review
    if (!needsDataSource && !isControlWidget) return 2; // Type, Config, Review
    if (selectedType === 'chart') return 4; // Type, Config, DataSource+ChartType, ChartFields, Review
    if (isControlWidget) return 4; // Type, Config, DataSource, ConfigureFilters, Review
    return 3; // Type, Config, DataSource, Review
  };

  const canProceed = () => {
    switch (activeStep) {
      case 1:
        return canProceedFromConfig;
      case 2:
        if (isImageWidget) {
          return imageConfig.image_url.trim().length > 0;
        } else if (selectedType === 'chart') {
          return canProceedFromDataSource;
        } else if (needsDataSource || isControlWidget) {
          return canProceedFromDataSource;
        }
        return true;
      case 3:
        if (selectedType === 'chart') {
          return canProceedFromChart;
        }
        // For control widgets, allow proceeding even with no filters (they can add later)
        return true;
      default:
        return true;
    }
  };

  const isLastStep = activeStep === getMaxStep();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Widget</DialogTitle>

      <DialogContent>
        {activeStep > 0 && (
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {renderStepContent()}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error creating widget: {error?.message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isLoading}>
            Back
          </Button>
        )}
        <Button onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button
            onClick={isLastStep ? handleCreate : handleNext}
            variant="contained"
            disabled={!canProceed() || isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isLastStep ? (
              'Create Widget'
            ) : (
              'Next'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
